import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { Wrench, Plus, Pencil, Trash2, CheckCircle2, AlertTriangle, Calendar as CalendarIcon, Clock, Cog, Boxes, Upload, X, FileSignature, Eye, History, Download } from "lucide-react";
import { showError } from "../lib/errorHandler";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQUENCIES = [
  { value: "daily", label: "Diaria", days: 1 },
  { value: "weekly", label: "Semanal", days: 7 },
  { value: "monthly", label: "Mensal", days: 30 },
  { value: "quarterly", label: "Trimestral", days: 90 },
  { value: "semi_annual", label: "Semestral", days: 180 },
  { value: "yearly", label: "Anual", days: 365 },
];

const EMPTY_FORM = {
  target_type: "machine",
  target_id: "",
  name: "",
  description: "",
  frequency: "monthly",
  interval_days: "",
  alert_days_before: 7,
  tasks: [],
  estimated_duration_minutes: 60,
  last_performed_at: "",
  active: true,
};

const statusColor = (status) => {
  if (status === "overdue") return "bg-red-100 text-red-800 border-red-300";
  if (status === "due_soon") return "bg-amber-100 text-amber-800 border-amber-300";
  return "bg-green-100 text-green-800 border-green-300";
};

const statusLabel = (status, days) => {
  if (status === "overdue") return `Vencido ${Math.abs(days)}d`;
  if (status === "due_soon") return `Em ${days}d`;
  if (days != null) return `Em ${days}d`;
  return "OK";
};

export default function PreventiveMaintenance() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [machines, setMachines] = useState([]);
  const [molds, setMolds] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [taskInput, setTaskInput] = useState("");
  const [showCompleteDialog, setShowCompleteDialog] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [completeNotes, setCompleteNotes] = useState("");
  const [completeCost, setCompleteCost] = useState(0);
  const [filterTarget, setFilterTarget] = useState("all");
  // Assinatura + imagens
  const [beforeImages, setBeforeImages] = useState([]);
  const [afterImages, setAfterImages] = useState([]);
  const [signatureConfirmed, setSignatureConfirmed] = useState(false);
  const [signedAt, setSignedAt] = useState("");
  const [uploadingImg, setUploadingImg] = useState(false);
  // Historico
  const [showLogs, setShowLogs] = useState(false);
  const [logsForPlan, setLogsForPlan] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logViewing, setLogViewing] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [plansRes, machRes, moldRes] = await Promise.all([
        axios.get(`${API}/preventive-maintenance/plans`),
        axios.get(`${API}/machines`),
        axios.get(`${API}/molds`),
      ]);
      setPlans(plansRes.data);
      setMachines(machRes.data);
      setMolds(moldRes.data);
    } catch (e) { console.error(e); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setTaskInput("");
    setShowForm(true);
  };

  const openEdit = (p) => {
    setEditing(p);
    setForm({
      target_type: p.target_type,
      target_id: p.target_id,
      name: p.name,
      description: p.description || "",
      frequency: p.frequency,
      interval_days: p.interval_days || "",
      alert_days_before: p.alert_days_before,
      tasks: p.tasks || [],
      estimated_duration_minutes: p.estimated_duration_minutes || 60,
      last_performed_at: p.last_performed_at ? p.last_performed_at.slice(0, 10) : "",
      active: p.active !== false,
    });
    setShowForm(true);
  };

  const submit = async () => {
    if (!form.target_id || !form.name) { toast.error("Selecione um alvo e nome do plano"); return; }
    try {
      const payload = {
        ...form,
        interval_days: form.interval_days ? parseInt(form.interval_days) : null,
        last_performed_at: form.last_performed_at ? new Date(form.last_performed_at).toISOString() : null,
        alert_days_before: parseInt(form.alert_days_before) || 7,
        estimated_duration_minutes: parseInt(form.estimated_duration_minutes) || 60,
      };
      if (editing) {
        await axios.put(`${API}/preventive-maintenance/plans/${editing.id}`, payload);
        toast.success("Plano actualizado");
      } else {
        await axios.post(`${API}/preventive-maintenance/plans`, payload);
        toast.success("Plano criado");
      }
      setShowForm(false);
      fetchData();
    } catch (e) {
      showError(e, "Erro ao guardar");
    }
  };

  const deletePlan = async (id) => {
    if (!window.confirm("Eliminar este plano de manutencao?")) return;
    try {
      await axios.delete(`${API}/preventive-maintenance/plans/${id}`);
      toast.success("Plano eliminado");
      fetchData();
    } catch (e) {
      showError(e, "Erro ao eliminar");
    }
  };

  const openComplete = (p) => {
    setCompleting(p);
    setCompleteNotes("");
    setCompleteCost(0);
    setBeforeImages([]);
    setAfterImages([]);
    setSignatureConfirmed(false);
    setSignedAt("");
    setShowCompleteDialog(true);
  };

  const submitComplete = async () => {
    if (!signatureConfirmed) {
      toast.error("E necessario confirmar a assinatura digital");
      return;
    }
    try {
      await axios.post(`${API}/preventive-maintenance/plans/${completing.id}/complete`, {
        plan_id: completing.id,
        notes: completeNotes,
        cost: parseFloat(completeCost) || 0,
        before_images: beforeImages,
        after_images: afterImages,
        signature_confirmed: signatureConfirmed,
      });
      toast.success("Manutencao registada e assinada");
      setShowCompleteDialog(false);
      fetchData();
    } catch (e) {
      showError(e, "Erro ao registar");
    }
  };

  const uploadImage = async (e, target) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    setUploadingImg(true);
    try {
      const uploaded = [];
      for (let i = 0; i < files.length; i++) {
        const fd = new FormData();
        fd.append("file", files[i]);
        const res = await axios.post(`${API}/preventive-maintenance/upload-image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push({ url: res.data.url, filename: res.data.filename });
      }
      if (target === "before") setBeforeImages(prev => [...prev, ...uploaded]);
      else setAfterImages(prev => [...prev, ...uploaded]);
      toast.success(`${uploaded.length} imagem(ns) carregada(s)`);
    } catch (err) {
      toast.error("Erro ao carregar imagem");
    } finally {
      setUploadingImg(false);
      e.target.value = "";
    }
  };

  const removeImg = (target, idx) => {
    if (target === "before") setBeforeImages(prev => prev.filter((_, i) => i !== idx));
    else setAfterImages(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSign = () => {
    setSignedAt(new Date().toISOString());
    setSignatureConfirmed(true);
  };

  const openLogs = async (plan) => {
    setShowLogs(true);
    setLogsForPlan([]);
    setLogsLoading(true);
    setLogViewing(plan);
    try {
      const res = await axios.get(`${API}/preventive-maintenance/logs`, { params: { plan_id: plan.id } });
      setLogsForPlan(res.data);
    } catch (e) {
      toast.error("Erro ao carregar historico");
    } finally {
      setLogsLoading(false);
    }
  };

  const exportLogsPDF = () => {
    if (!logViewing || logsForPlan.length === 0) {
      toast.error("Nada para exportar");
      return;
    }
    const win = window.open("", "_blank");
    if (!win) { toast.error("Permita popups para exportar PDF"); return; }
    const today = new Date().toLocaleString("pt-PT");
    const freqLabel = FREQUENCIES.find(f => f.value === logViewing.frequency)?.label || logViewing.frequency;
    
    const logsHtml = logsForPlan.map(l => {
      const beforeImgs = (l.before_images || []).map(img =>
        `<div style="text-align:center;border:1px solid #eee;border-radius:6px;padding:6px"><img src="${img.url}" style="max-width:100%;max-height:140px;border-radius:4px"/><p style="font-size:9px;color:#666;margin:4px 0 0">${img.filename || ''}</p></div>`
      ).join("");
      const afterImgs = (l.after_images || []).map(img =>
        `<div style="text-align:center;border:1px solid #eee;border-radius:6px;padding:6px"><img src="${img.url}" style="max-width:100%;max-height:140px;border-radius:4px"/><p style="font-size:9px;color:#666;margin:4px 0 0">${img.filename || ''}</p></div>`
      ).join("");
      
      return `<div class="log-entry">
        <div class="log-header">
          <div>
            <div class="log-date">${new Date(l.performed_at).toLocaleString("pt-PT")}</div>
            <div class="log-sig">✓ Assinado por <strong>${l.signature_name || l.performed_by_name}</strong> (${l.signature_user_code || '-'}) em ${l.signed_at ? new Date(l.signed_at).toLocaleString("pt-PT") : '-'}</div>
          </div>
          <div class="log-meta">
            <div>Duracao: <strong>${l.duration_minutes} min</strong></div>
            ${l.cost > 0 ? `<div>Custo: <strong>${l.cost.toFixed(2)} EUR</strong></div>` : ''}
          </div>
        </div>
        ${l.notes ? `<div class="log-notes">"${l.notes}"</div>` : ''}
        ${l.parts_replaced && l.parts_replaced.length > 0 ? `<div class="log-parts"><strong>Pecas substituidas:</strong> ${l.parts_replaced.join(", ")}</div>` : ''}
        ${(beforeImgs || afterImgs) ? `<div class="log-images">
          <div><div class="img-label img-before">ANTES (${l.before_images?.length || 0})</div><div class="img-grid">${beforeImgs || '<div class="img-empty">Sem fotos</div>'}</div></div>
          <div><div class="img-label img-after">DEPOIS (${l.after_images?.length || 0})</div><div class="img-grid">${afterImgs || '<div class="img-empty">Sem fotos</div>'}</div></div>
        </div>` : ''}
      </div>`;
    }).join("");

    win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Historico Manutencao - ${logViewing.target_code}</title>
<style>
body{font-family:'Segoe UI',Arial,sans-serif;padding:30px;max-width:1000px;margin:0 auto;color:#333;font-size:13px}
.header{display:flex;justify-content:space-between;align-items:flex-start;border-bottom:3px solid #1e3a8a;padding-bottom:14px;margin-bottom:20px}
.header img{height:50px}
h1{font-size:20px;color:#1e3a8a;margin:0}
.subtitle{color:#666;font-size:11px;margin-top:4px}
.plan-info{background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;padding:14px;margin-bottom:20px}
.plan-info h2{margin:0 0 8px;font-size:15px;color:#1e3a8a}
.plan-info p{margin:3px 0;font-size:12px;color:#555}
.plan-info strong{color:#111}
.log-entry{border:1px solid #e5e7eb;border-radius:8px;padding:14px;margin-bottom:14px;page-break-inside:avoid}
.log-header{display:flex;justify-content:space-between;align-items:flex-start;flex-wrap:wrap;gap:8px;margin-bottom:10px;padding-bottom:8px;border-bottom:1px solid #eee}
.log-date{font-size:14px;font-weight:bold;color:#111}
.log-sig{font-size:11px;color:#1e40af;margin-top:3px}
.log-meta{font-size:11px;color:#555;text-align:right}
.log-notes{font-style:italic;background:#f9fafb;padding:8px 10px;border-left:3px solid #94a3b8;font-size:12px;color:#444;margin:8px 0}
.log-parts{font-size:11px;color:#555;margin:6px 0}
.log-images{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-top:10px}
.img-label{font-size:10px;text-transform:uppercase;font-weight:bold;letter-spacing:0.5px;margin-bottom:4px}
.img-before{color:#b45309}
.img-after{color:#166534}
.img-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
.img-empty{font-size:10px;color:#999;padding:10px;text-align:center;border:1px dashed #ddd;border-radius:4px}
.footer{margin-top:30px;padding-top:12px;border-top:1px solid #e5e7eb;font-size:10px;color:#999;text-align:center}
@media print{body{padding:14px;font-size:11px}.log-entry{page-break-inside:avoid}}
</style></head><body>
<div class="header">
  <div style="display:flex;align-items:center;gap:12px">
    <img src="/ep3d-logo.png" onerror="this.style.display='none'">
    <div>
      <h1>Historico de Manutencao Preventiva</h1>
      <div class="subtitle">EP3D ENGINEERING PROJECT | NIF: 517 316 188 | Documento ISO 9001</div>
    </div>
  </div>
  <div style="text-align:right;font-size:11px;color:#666">
    <div><strong>Gerado em:</strong> ${today}</div>
    <div><strong>Registos:</strong> ${logsForPlan.length}</div>
  </div>
</div>

<div class="plan-info">
  <h2>${logViewing.target_code} &mdash; ${logViewing.name}</h2>
  <p><strong>${logViewing.target_type === 'machine' ? 'Maquina' : 'Molde'}:</strong> ${logViewing.target_name}</p>
  <p><strong>Frequencia:</strong> ${freqLabel}${logViewing.interval_days ? ' (' + logViewing.interval_days + ' dias)' : ''}</p>
  ${logViewing.description ? `<p><strong>Descricao:</strong> ${logViewing.description}</p>` : ''}
  ${logViewing.tasks?.length > 0 ? `<p><strong>Tarefas:</strong> ${logViewing.tasks.join(", ")}</p>` : ''}
</div>

<h2 style="font-size:15px;color:#1e3a8a;border-bottom:2px solid #e5e7eb;padding-bottom:6px;margin:20px 0 12px">Registos Cronologicos (${logsForPlan.length})</h2>
${logsHtml}

<div class="footer">
  Documento gerado pelo Sistema MES EP3D para fins de auditoria ISO 9001 | ${today}<br>
  Cada registo contem assinatura digital do operador, data/hora, observacoes e fotos antes/depois quando disponiveis.
</div>
</body></html>`);
    win.document.close();
    setTimeout(() => win.print(), 600);
  };

  const addTask = () => {
    if (!taskInput.trim()) return;
    setForm(f => ({ ...f, tasks: [...f.tasks, taskInput.trim()] }));
    setTaskInput("");
  };

  const removeTask = (idx) => {
    setForm(f => ({ ...f, tasks: f.tasks.filter((_, i) => i !== idx) }));
  };

  const filteredPlans = filterTarget === "all" ? plans : plans.filter(p => p.target_type === filterTarget);
  const overdue = filteredPlans.filter(p => p.status === "overdue");
  const dueSoon = filteredPlans.filter(p => p.status === "due_soon");
  const ok = filteredPlans.filter(p => p.status === "ok");

  const targetOptions = form.target_type === "machine" ? machines : molds;

  return (
    <div className="space-y-6" data-testid="pm-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-2">
            <Wrench className="h-8 w-8 text-amber-600" />
            Manutencao Preventiva
          </h1>
          <p className="text-gray-500 mt-1 text-sm">Calendario de manutencoes para maquinas e moldes com alertas automaticos</p>
        </div>
        <div className="flex items-center gap-2">
          <select
            value={filterTarget}
            onChange={(e) => setFilterTarget(e.target.value)}
            className="border rounded-lg px-3 py-2 text-sm bg-white"
            data-testid="pm-filter-target"
          >
            <option value="all">Todos</option>
            <option value="machine">Maquinas</option>
            <option value="mold">Moldes</option>
          </select>
          <Button onClick={openNew} data-testid="pm-new-btn"><Plus className="h-4 w-4 mr-2" />Novo Plano</Button>
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card className="border-l-4 border-l-red-500" data-testid="pm-kpi-overdue">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">Vencidos</div>
              <div className="text-3xl font-bold text-red-600 mt-1">{overdue.length}</div>
            </div>
            <AlertTriangle className="h-10 w-10 text-red-300" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-amber-500" data-testid="pm-kpi-duesoon">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">A Vencer</div>
              <div className="text-3xl font-bold text-amber-600 mt-1">{dueSoon.length}</div>
            </div>
            <Clock className="h-10 w-10 text-amber-300" />
          </CardContent>
        </Card>
        <Card className="border-l-4 border-l-green-500" data-testid="pm-kpi-ok">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <div className="text-xs font-semibold text-gray-500 uppercase">Em Dia</div>
              <div className="text-3xl font-bold text-green-600 mt-1">{ok.length}</div>
            </div>
            <CheckCircle2 className="h-10 w-10 text-green-300" />
          </CardContent>
        </Card>
      </div>

      {/* Tabela de planos */}
      <Card>
        <CardHeader><CardTitle className="flex items-center gap-2"><CalendarIcon className="h-5 w-5" />Planos ({filteredPlans.length})</CardTitle></CardHeader>
        <CardContent>
          {filteredPlans.length === 0 ? (
            <div className="text-center py-8 text-gray-400">Nenhum plano configurado. Crie um novo plano para comecar.</div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Estado</TableHead>
                  <TableHead>Alvo</TableHead>
                  <TableHead>Nome</TableHead>
                  <TableHead>Frequencia</TableHead>
                  <TableHead>Ultima</TableHead>
                  <TableHead>Proxima</TableHead>
                  <TableHead className="text-right">Acoes</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredPlans.map(p => (
                  <TableRow key={p.id} data-testid={`pm-row-${p.id}`}>
                    <TableCell>
                      <Badge className={`text-xs ${statusColor(p.status)}`}>
                        {statusLabel(p.status, p.days_until_due)}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-xs">
                        {p.target_type === "machine" ? <Cog className="h-4 w-4 text-blue-500" /> : <Boxes className="h-4 w-4 text-purple-500" />}
                        <div>
                          <div className="font-mono font-bold">{p.target_code}</div>
                          <div className="text-gray-500 text-[10px]">{p.target_name}</div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="font-medium">{p.name}</TableCell>
                    <TableCell className="text-xs">{FREQUENCIES.find(f => f.value === p.frequency)?.label || p.frequency}</TableCell>
                    <TableCell className="text-xs text-gray-600">{p.last_performed_at ? new Date(p.last_performed_at).toLocaleDateString("pt-PT") : "Nunca"}</TableCell>
                    <TableCell className="text-xs">{p.next_due_at ? new Date(p.next_due_at).toLocaleDateString("pt-PT") : "-"}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openComplete(p)} title="Concluir Manutencao" className="text-green-600" data-testid={`pm-complete-${p.id}`}>
                          <CheckCircle2 className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openLogs(p)} title="Historico" className="text-indigo-600" data-testid={`pm-history-${p.id}`}>
                          <History className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => openEdit(p)} title="Editar"><Pencil className="h-4 w-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => deletePlan(p.id)} title="Eliminar" className="text-red-500"><Trash2 className="h-4 w-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Form Dialog */}
      <Dialog open={showForm} onOpenChange={setShowForm}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar Plano" : "Novo Plano de Manutencao"}</DialogTitle></DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600">Tipo de Alvo *</label>
                <select
                  value={form.target_type}
                  onChange={(e) => setForm({ ...form, target_type: e.target.value, target_id: "" })}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="pm-target-type"
                >
                  <option value="machine">Maquina</option>
                  <option value="mold">Molde</option>
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600">{form.target_type === "machine" ? "Maquina" : "Molde"} *</label>
                <select
                  value={form.target_id}
                  onChange={(e) => setForm({ ...form, target_id: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="pm-target-id"
                >
                  <option value="">Seleccionar...</option>
                  {targetOptions.map(o => <option key={o.id} value={o.id}>{o.code} - {o.description}</option>)}
                </select>
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600">Nome do Plano *</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Lubrificacao geral, Limpeza de canais..."
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                data-testid="pm-name"
              />
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600">Descricao</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                rows={2}
                className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
              />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600">Frequencia *</label>
                <select
                  value={form.frequency}
                  onChange={(e) => setForm({ ...form, frequency: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  data-testid="pm-frequency"
                >
                  {FREQUENCIES.map(f => <option key={f.value} value={f.value}>{f.label} ({f.days}d)</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600">Override (dias)</label>
                <input
                  type="number"
                  min="1"
                  value={form.interval_days}
                  onChange={(e) => setForm({ ...form, interval_days: e.target.value })}
                  placeholder="Opcional"
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600">Alertar (dias antes)</label>
                <input
                  type="number"
                  min="0"
                  value={form.alert_days_before}
                  onChange={(e) => setForm({ ...form, alert_days_before: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-bold text-gray-600">Ultima Manutencao</label>
                <input
                  type="date"
                  value={form.last_performed_at}
                  onChange={(e) => setForm({ ...form, last_performed_at: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
              <div>
                <label className="text-xs font-bold text-gray-600">Duracao estimada (min)</label>
                <input
                  type="number"
                  min="1"
                  value={form.estimated_duration_minutes}
                  onChange={(e) => setForm({ ...form, estimated_duration_minutes: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                />
              </div>
            </div>
            <div>
              <label className="text-xs font-bold text-gray-600">Tarefas</label>
              <div className="flex gap-2 mt-1">
                <input
                  value={taskInput}
                  onChange={(e) => setTaskInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTask(); } }}
                  placeholder="Adicionar tarefa..."
                  className="flex-1 border rounded-lg px-3 py-2 text-sm"
                  data-testid="pm-task-input"
                />
                <Button type="button" size="sm" onClick={addTask}>+</Button>
              </div>
              {form.tasks.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {form.tasks.map((t, i) => (
                    <li key={i} className="flex items-center justify-between text-sm bg-slate-50 rounded px-2 py-1">
                      <span>• {t}</span>
                      <button type="button" onClick={() => removeTask(i)} className="text-red-500 hover:text-red-700 text-xs">x</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div className="flex justify-end gap-2 pt-3 border-t">
              <Button variant="outline" onClick={() => setShowForm(false)}>Cancelar</Button>
              <Button onClick={submit} data-testid="pm-submit">{editing ? "Guardar" : "Criar Plano"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Complete Dialog */}
      <Dialog open={showCompleteDialog} onOpenChange={setShowCompleteDialog}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-700">
              <CheckCircle2 className="h-5 w-5" />Concluir Manutencao + Assinatura Digital
            </DialogTitle>
          </DialogHeader>
          {completing && (
            <div className="space-y-4 mt-2">
              <div className="bg-slate-50 rounded p-3 text-sm">
                <div className="font-bold">{completing.target_code} - {completing.name}</div>
                <div className="text-xs text-gray-500 mt-1">{completing.target_name}</div>
              </div>

              {completing.tasks?.length > 0 && (
                <div>
                  <div className="text-xs font-bold text-gray-600 mb-1">Tarefas a Verificar</div>
                  <ul className="space-y-1 text-sm">
                    {completing.tasks.map((t, i) => <li key={i}>• {t}</li>)}
                  </ul>
                </div>
              )}

              {/* Imagens ANTES */}
              <div className="border rounded-lg p-3 bg-amber-50/40 border-amber-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-amber-900 uppercase">Fotos ANTES da intervencao</div>
                  <span className="text-[10px] text-amber-700">{beforeImages.length} foto(s)</span>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-amber-300 rounded-md cursor-pointer hover:bg-amber-50 text-xs font-medium text-amber-900" data-testid="pm-upload-before">
                  <Upload className="h-3.5 w-3.5" />{uploadingImg ? "A carregar..." : "Carregar"}
                  <input type="file" accept="image/*" multiple onChange={(e) => uploadImage(e, "before")} className="hidden" disabled={uploadingImg} />
                </label>
                {beforeImages.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {beforeImages.map((img, i) => (
                      <div key={i} className="relative border rounded-md overflow-hidden group">
                        <img src={img.url} alt={img.filename} className="w-full h-20 object-cover" />
                        <button onClick={() => removeImg("before", i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Imagens DEPOIS */}
              <div className="border rounded-lg p-3 bg-green-50/40 border-green-200">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-xs font-bold text-green-900 uppercase">Fotos DEPOIS da intervencao</div>
                  <span className="text-[10px] text-green-700">{afterImages.length} foto(s)</span>
                </div>
                <label className="inline-flex items-center gap-2 px-3 py-1.5 bg-white border border-green-300 rounded-md cursor-pointer hover:bg-green-50 text-xs font-medium text-green-900" data-testid="pm-upload-after">
                  <Upload className="h-3.5 w-3.5" />{uploadingImg ? "A carregar..." : "Carregar"}
                  <input type="file" accept="image/*" multiple onChange={(e) => uploadImage(e, "after")} className="hidden" disabled={uploadingImg} />
                </label>
                {afterImages.length > 0 && (
                  <div className="grid grid-cols-5 gap-2 mt-2">
                    {afterImages.map((img, i) => (
                      <div key={i} className="relative border rounded-md overflow-hidden group">
                        <img src={img.url} alt={img.filename} className="w-full h-20 object-cover" />
                        <button onClick={() => removeImg("after", i)} className="absolute top-0.5 right-0.5 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-[10px] opacity-0 group-hover:opacity-100">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-bold text-gray-600">Observacoes</label>
                  <textarea
                    value={completeNotes}
                    onChange={(e) => setCompleteNotes(e.target.value)}
                    rows={3}
                    className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                    data-testid="pm-complete-notes"
                  />
                </div>
                <div>
                  <label className="text-xs font-bold text-gray-600">Custo (EUR)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={completeCost}
                    onChange={(e) => setCompleteCost(e.target.value)}
                    className="w-full border rounded-lg px-3 py-2 text-sm mt-1"
                  />
                </div>
              </div>

              {/* Assinatura digital */}
              <div className={`border-2 rounded-lg p-4 transition-all ${signatureConfirmed ? "bg-blue-50 border-blue-400" : "bg-slate-50 border-dashed border-slate-300"}`}>
                <div className="flex items-start gap-3">
                  <FileSignature className={`h-6 w-6 mt-0.5 ${signatureConfirmed ? "text-blue-600" : "text-slate-400"}`} />
                  <div className="flex-1">
                    <div className="text-sm font-bold text-gray-800 mb-1">Assinatura Digital ISO 9001</div>
                    {!signatureConfirmed ? (
                      <>
                        <p className="text-xs text-gray-600 mb-3">Ao confirmar, declaro que executei a manutencao conforme as tarefas indicadas e que os registos sao verdadeiros.</p>
                        <Button
                          type="button"
                          onClick={handleSign}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid="pm-sign-btn"
                        >
                          <FileSignature className="h-4 w-4 mr-2" />
                          Assinar como {user?.name || user?.user_code}
                        </Button>
                      </>
                    ) : (
                      <div data-testid="pm-signed-ok" className="text-sm">
                        <div className="text-blue-900 font-bold">✓ Assinado por {user?.name || user?.user_code} ({user?.user_code})</div>
                        <div className="text-xs text-blue-700 mt-1">{new Date(signedAt).toLocaleString("pt-PT")}</div>
                        <button type="button" onClick={() => { setSignatureConfirmed(false); setSignedAt(""); }} className="text-[11px] text-blue-600 underline mt-1">Limpar assinatura</button>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button variant="outline" onClick={() => setShowCompleteDialog(false)}>Cancelar</Button>
                <Button onClick={submitComplete} disabled={!signatureConfirmed} className="bg-green-600 hover:bg-green-700 disabled:opacity-50" data-testid="pm-complete-submit">
                  <CheckCircle2 className="h-4 w-4 mr-2" />Registar com Assinatura
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Logs Dialog */}
      <Dialog open={showLogs} onOpenChange={setShowLogs}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div>
                <DialogTitle className="flex items-center gap-2 text-indigo-700"><History className="h-5 w-5" />Historico de Manutencoes</DialogTitle>
                {logViewing && <p className="text-xs text-gray-500 mt-1">{logViewing.target_code} - {logViewing.name}</p>}
              </div>
              <Button
                onClick={exportLogsPDF}
                variant="outline"
                size="sm"
                disabled={logsLoading || logsForPlan.length === 0}
                data-testid="pm-logs-export-pdf"
                className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
              >
                <Download className="h-4 w-4 mr-2" />Exportar PDF (ISO 9001)
              </Button>
            </div>
          </DialogHeader>
          <div className="mt-3 space-y-3">
            {logsLoading ? (
              <div className="text-center py-8 text-gray-400 text-sm">A carregar...</div>
            ) : logsForPlan.length === 0 ? (
              <div className="text-center py-8 text-gray-400 text-sm">Sem registos historicos</div>
            ) : (
              logsForPlan.map(l => (
                <div key={l.id} className="border rounded-lg p-3 bg-white" data-testid={`pm-log-${l.id}`}>
                  <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
                    <div>
                      <div className="text-sm font-bold text-gray-900">{new Date(l.performed_at).toLocaleString("pt-PT")}</div>
                      <div className="text-xs text-gray-600 flex items-center gap-1.5 mt-0.5">
                        <FileSignature className="h-3 w-3 text-blue-600" />
                        Assinado por <span className="font-semibold">{l.signature_name || l.performed_by_name}</span> ({l.signature_user_code || "-"})
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-xs text-gray-500">Duracao: <strong>{l.duration_minutes} min</strong></div>
                      {l.cost > 0 && <div className="text-xs text-gray-500">Custo: <strong>{l.cost.toFixed(2)} EUR</strong></div>}
                    </div>
                  </div>
                  {l.notes && <p className="text-sm text-gray-700 italic bg-slate-50 rounded px-2 py-1.5 my-2">"{l.notes}"</p>}
                  {(l.before_images?.length > 0 || l.after_images?.length > 0) && (
                    <div className="grid grid-cols-2 gap-3 mt-2">
                      <div>
                        <div className="text-[10px] font-bold text-amber-700 uppercase mb-1">Antes ({l.before_images?.length || 0})</div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {(l.before_images || []).map((img, i) => (
                            <a key={i} href={img.url} target="_blank" rel="noreferrer" className="block">
                              <img src={img.url} alt={img.filename} className="w-full h-16 object-cover rounded border" />
                            </a>
                          ))}
                        </div>
                      </div>
                      <div>
                        <div className="text-[10px] font-bold text-green-700 uppercase mb-1">Depois ({l.after_images?.length || 0})</div>
                        <div className="grid grid-cols-3 gap-1.5">
                          {(l.after_images || []).map((img, i) => (
                            <a key={i} href={img.url} target="_blank" rel="noreferrer" className="block">
                              <img src={img.url} alt={img.filename} className="w-full h-16 object-cover rounded border" />
                            </a>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
